import React from "react";
import styles from "./EffectiveDateField.module.css";

type EffectiveDateFieldProps = {
  id: string;
  label?: string;
  value: string;
  onChange: (value: string) => void;
  className?: string;
  disabled?: boolean;
  helperText?: string;
  "data-testid"?: string;
};

const DEFAULT_HELPER =
  "This change will apply to reports for work on or after the selected effective date.";

export const EFFECTIVE_DATED_HISTORY_FEATURE = "effective_dated_history";

export const EFFECTIVE_HISTORY_TIER_RESTRICTED_MESSAGE =
  "Effective-dated history is available on Pro and Enterprise plans. Remove the effective date to save this as a current change, or upgrade to schedule/backdate changes.";

export const EFFECTIVE_HISTORY_FEATURE_LOADING_MESSAGE =
  "Subscription features are still loading. Try again in a moment.";

export const isValidYmd = (value: string) => /^\d{4}-\d{2}-\d{2}$/.test(value);

export const todayYmd = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
};

export const withEffectiveStartDate = <T extends Record<string, unknown>>(
  payload: T,
  effectiveStartDate?: string | null,
) => {
  if (!effectiveStartDate) return payload;
  if (!isValidYmd(effectiveStartDate)) return payload;
  return { ...payload, effectiveStartDate };
};

export const withEffectiveStartDateIfAllowed = <T extends Record<string, unknown>>(
  payload: T,
  effectiveStartDate: string | null | undefined,
  canUseEffectiveHistory: boolean,
) => (canUseEffectiveHistory ? withEffectiveStartDate(payload, effectiveStartDate) : payload);

export const getEffectiveHistoryErrorMessage = (err: unknown, fallback: string) => {
  const error = err as { code?: string; feature?: string; message?: string };
  if (error?.code === "TIER_RESTRICTED" && error.feature === EFFECTIVE_DATED_HISTORY_FEATURE) {
    return EFFECTIVE_HISTORY_TIER_RESTRICTED_MESSAGE;
  }
  return fallback;
};

const EffectiveDateField = ({
  id,
  label = "Effective Date",
  value,
  onChange,
  className,
  disabled,
  helperText = DEFAULT_HELPER,
  "data-testid": testId,
}: EffectiveDateFieldProps) => {
  return (
    <div className={`${styles["effective-date-field"]}${className ? ` ${className}` : ""}`}>
      <div className={styles["effective-date-label-row"]}>
        <label className={styles["effective-date-label"]} htmlFor={id}>
          {label}
        </label>
        <span
          className={styles["effective-date-tooltip"]}
          title={helperText}
          aria-label={helperText}
          data-tooltip={helperText}
          tabIndex={0}
        >
          ?
        </span>
      </div>
      <input
        id={id}
        className={styles["effective-date-control"]}
        type="date"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        data-testid={testId}
      />
    </div>
  );
};

export default EffectiveDateField;
